<template>
  <div>
    <div class="chart_holder" v-if="chartData">
      <plotly :data="chartData" :layout="chartLayout" :config="chartConfig" />
    </div>
  </div>
</template>

<script>
import { Plotly } from 'vue-plotly'
import moment from 'moment'
import defaultLayout from '@/components/helpers/plotly_style'
import ReportDataHelper from '@/components/helpers/ReportDataHelper'

export default {
  data () {
    return {
      // NOTE: These are auto-populated by the report parent.
      statsData: null,
      liveData: null,
      related_ids: null,
      reportParams: null,
      report: null,
      // End Note

    }
  },

  components: {
    Plotly
  },
  props: {
    layout: {
      type: Object,
      default: () => ({})
    },
    config: {
      type: Object,
      default: () => ({ responsive: true })
    },
    label_field: {
      type: String,
      default: null
    },
    statistics: {
      type: Array[String],
      default: async () => {
        return []
      }
    },
    order_field: {
      // With optional + or - prefix to indicate sort order
      type: String,
      default: null
    },
    data_key: {
      type: String,
      default: 'data'
    },
    null_values: {
      // type is any value
      default: 0
    },
    data_source: {
      type: String, // 'live' or 'stats'
      default: 'live'
    },
    is_date_axis: {
      // If true, interpret statsData.labels as date strings like "YYYY/MM/DD"
      // and produce date-based x-axis values
      type: Boolean,
      default: false
    },
    precision: {
      type: Number,
      default: 2 // Default precision to 2 decimal places
    },
    plotType: {
      type: String,
      default: 'bar' // 'bar' or 'scatter', etc
    },
    hideZeroValues: {
      type: Boolean,
      default: false
    },
    value_multiple: {
      type: Number,
      default: 1
    },
    aggregration_mode: {
      type: String,
      default: 'sum'
    },
    date_format: {
      type: String,
      default: "%Y-%m-%d"
    },
    highlightMax: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    async getRequiredStats () {
      if (this.data_source === 'stats') {
        // Combine statistics, label_field, and order_field into a list, flattening any nested arrays
        let stats = []
        if (this.statistics) {
          // If statistics is a promise, await it
          let newStats
          if (this.statistics instanceof Promise) {
            newStats = await this.statistics
          } else {
            newStats = this.statistics
          }
          stats = stats.concat(newStats)
          // Flatten
          stats = stats.flat()
        }
        if (this.label_field) {
          stats.push(this.label_field)
        }
        if (this.order_field) {
          stats.push(this.order_field)
        }
        return stats
      } else {
        return []
      }
    },
    async getRequiredDeviceProps () {
      if (this.data_source === 'live') {
        // Combine statistics, label_field, and order_field into a list, flattening any nested arrays
        let stats = []
        if (this.statistics) {
          // If statistics is a promise, await it
          let newStats
          if (this.statistics instanceof Promise) {
            newStats = await this.statistics
          } else {
            newStats = this.statistics
          }

          stats = stats.concat(newStats)
          // Flatten
          stats = stats.flat()
        }
        if (this.label_field) {
          stats.push(this.label_field)
        }
        if (this.order_field) {
          stats.push(this.order_field)
        }
        return stats
      } else {
        return []
      }
    }
  },
  computed: {
    chartData() {
      // 1) LIVE DATA => Single trace
      if (this.data_source === 'live') {
        if (!this.liveData || !this.liveData[this.data_key]) {
          return null
        }
        // We assume liveData[this.data_key] is an array or object we can iterate
        const values = Object.values(this.liveData[this.data_key])
        return ReportDataHelper.generateCategoricalPlotLiveData(
          values,
          this.label_field,
          this.statistics,
          this.order_field,
          this.null_values,
          this.plotType,
          this.hideZeroValues,
          this.value_multiple,
          this.aggregration_mode,
          this.highlightMax
        )
      } else {
        // 2) STATS DATA => Multiple traces (one per device)
        if (!this.statsData) {
          return null
        }
        // Pass the entire statsData object (which contains {labels, stats, etc.})
        // For multi-trace bar charts, set isDate to this.is_date_axis
        return ReportDataHelper.generateCategoricalPlotStatsData(
          this.statsData,
          this.statistics,       // e.g. 'DEVICE_DAILY_TOTAL_HOURS'
          this.null_values,
          this.is_date_axis,       // parse "YYYY/MM/DD" -> "YYYY-MM-DD"
          this.plotType,           // 'bar' or 'line'
          this.hideZeroValues,
          this.value_multiple,
          this.aggregration_mode,
          this.highlightMax
        )
      }
    },
    chartLayout() {
      let newLayout = {
        ...defaultLayout,
        ...this.layout,
      }
      
      // Force categorical axis for consistent label placement
      newLayout.xaxis.type = 'category'
      newLayout.xaxis.automargin = true
      newLayout.xaxis.tickmode = 'array'
      newLayout.xaxis.tickvals = this.chartData[0].x
      
      if (this.is_date_axis) {
        const formattedDates = this.chartData[0].x.map(dateStr => {
          try {
            // Parse the date string with moment
            const momentDate = moment(dateStr);
            
            // Check if date is valid
            if (!momentDate.isValid()) {
              return dateStr; // Return original if parsing fails
            }
            
            // Format using specified format or default to "ddd MMM D"
            return momentDate.format(this.date_format || 'ddd MMM D');
          } catch (e) {
            // If any error occurs, return the original string
            return dateStr;
          }
        });
        
        newLayout.xaxis.ticktext = formattedDates;
      } else {
        newLayout.xaxis.ticktext = this.chartData[0].x;
      }
      
      return newLayout;
    },
    chartConfig() {
      return this.config
    }
  }
}
</script>

<style scoped>
div.chart_holder {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
</style>
